import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { cn } from '../utils';

const buttonVariants = cva(
  cn(
    // Base layout
    'relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[4px]',
    'font-medium transition-colors',
    // Focus + disabled
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg)]',
    'disabled:pointer-events-none disabled:opacity-50',
    // Slot icon sizing
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  ),
  {
    variants: {
      variant: {
        primary: cn(
          'bg-[var(--color-accent)] text-[var(--color-bg)]',
          'hover:bg-[var(--color-accent-hover)]',
          'active:bg-[var(--color-accent)]',
        ),
        secondary: cn(
          'border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text-primary)]',
          'hover:bg-[var(--color-surface)]',
          'active:bg-[var(--color-surface)]',
        ),
        outline: cn(
          'border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text-primary)]',
          'hover:bg-[var(--color-surface)]',
          'active:bg-[var(--color-surface)]',
        ),
        ghost: cn(
          'bg-transparent text-[var(--color-text-primary)]',
          'hover:bg-[var(--color-surface)]',
          'active:bg-[var(--color-surface)]',
        ),
        destructive: cn(
          'bg-transparent text-[var(--color-error)]',
          'hover:bg-[color-mix(in_oklch,var(--color-error)_10%,transparent)]',
          'active:bg-[color-mix(in_oklch,var(--color-error)_15%,transparent)]',
          'focus-visible:ring-[var(--color-error)]',
        ),
        link: cn(
          'bg-transparent px-0 text-[var(--color-accent)] underline-offset-4',
          'hover:underline',
          'h-auto',
        ),
      },
      size: {
        sm: 'h-7 px-3 text-[12px]',
        md: 'h-9 px-4 text-[13px]',
        lg: 'h-11 px-6 text-[14px]',
        icon: 'h-9 w-9 p-0',
      },
    },
    compoundVariants: [
      {
        variant: 'link',
        size: ['sm', 'md', 'lg'],
        className: 'h-auto px-0',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Shows a spinner, disables interaction, preserves width. */
  loading?: boolean;
  /** Icon rendered before content (hidden while loading). */
  leftIcon?: React.ReactNode;
  /** Icon rendered after content (hidden while loading). */
  rightIcon?: React.ReactNode;
  /** Override the spinner label (defaults to 'Loading'). */
  loadingLabel?: string;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      leftIcon,
      rightIcon,
      loadingLabel = 'Loading',
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    // asChild requires a single child — loading spinner overlay only applies to the button path.
    if (asChild) {
      return (
        <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props}>
          {children}
        </Comp>
      );
    }

    return (
      <button
        ref={ref}
        type={props.type ?? 'button'}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        data-loading={loading || undefined}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      >
        {loading ? (
          <Loader2 className="absolute size-4 animate-spin" aria-label={loadingLabel} />
        ) : null}
        <span
          className={cn('inline-flex items-center justify-center gap-2', loading && 'invisible')}
        >
          {leftIcon}
          {children}
          {rightIcon}
        </span>
      </button>
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
